// frontend/src/pages/ErrorTestPage.tsx
// Optional test page to verify error handling system
// Add this temporarily to test all error scenarios

import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useErrorHandler } from '../errors';
import { PageError } from '../components/common/PageError';
import { InlineMessage } from '../components/common/InlineMessage';
import { clientsAPI } from '../services/api';
import { colors } from '../theme/theme';

const TestContainer = styled(Container)({
  paddingTop: '2rem',
  paddingBottom: '2rem',
});

const TestSection = styled(Paper)({
  padding: '2rem',
  marginBottom: '2rem',
  border: `2px solid ${colors.primary.light}`,
});

const TestButton = styled(Button)({
  margin: '0.5rem',
  minWidth: '200px',
});

const ErrorTestPage: React.FC = () => {
  const { handleError } = useErrorHandler();
  const [showPageError, setShowPageError] = useState(false);
  const [showInlineError, setShowInlineError] = useState(false);
  const [pageErrorMessage, setPageErrorMessage] = useState('');

  // Test 1: Network Error (simulate server down)
  const testNetworkError = async () => {
    setShowPageError(false);
    try {
      await clientsAPI.getAll();
    } catch (error) {
      setPageErrorMessage('Failed to load clients. Please try again.');
      setShowPageError(true);
      handleError(error, {
        context: 'testing network error',
        showToast: false,
      });
    }
  };

  // Test 2: Toast Error
  const testToastError = () => {
    handleError(new Error('This is a toast error test'), {
      context: 'testing toast',
      showToast: true,
    });
  };

  // Test 3: Inline Error
  const testInlineError = () => {
    setShowInlineError(true);
    setTimeout(() => setShowInlineError(false), 5000);
  };

  // Test 4: Error Boundary (this will crash React)
  const testErrorBoundary = () => {
    throw new Error('Testing ErrorBoundary - intentional crash!');
  };

  // Test 5: Different Error Types
  const testStringError = () => {
    handleError('This is a string error', {
      context: 'testing string error',
      showToast: true,
    });
  };

  const test404Error = async () => {
    try {
      await clientsAPI.getById(99999);
    } catch (error) {
      handleError(error, {
        context: 'testing 404 error',
        showToast: true,
      });
    }
  };

  const clearPageError = () => {
    setShowPageError(false);
    setPageErrorMessage('');
  };

  return (
    <TestContainer maxWidth="lg">
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        Error System Test Page
      </Typography>

      {/* Instructions */}
      <TestSection>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          📋 Testing Instructions
        </Typography>
        <Typography variant="body1" paragraph>
          Use the buttons below to test different error scenarios. Check the
          console for detailed error logs and watch for UI feedback.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> For network error tests to work properly, make
          sure your backend server is stopped. For 404 tests, the server should
          be running.
        </Typography>
      </TestSection>

      {/* Test Section 1: Network Errors */}
      <TestSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          🌐 Network Error Tests
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Kill your dotnet server first, then click these buttons
        </Typography>
        <Box>
          <TestButton
            variant="contained"
            color="primary"
            onClick={testNetworkError}
          >
            Test Network Error (PageError)
          </TestButton>
          <TestButton
            variant="outlined"
            color="secondary"
            onClick={clearPageError}
          >
            Clear Page Error
          </TestButton>
        </Box>
      </TestSection>

      {/* Show PageError if triggered */}
      {showPageError && (
        <TestSection>
          <Typography variant="subtitle2" gutterBottom>
            PageError Component Test:
          </Typography>
          <PageError message={pageErrorMessage} onRetry={testNetworkError} />
        </TestSection>
      )}

      {/* Test Section 2: Toast Errors */}
      <TestSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          🍞 Toast Error Tests
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          These will show toast notifications
        </Typography>
        <Box>
          <TestButton
            variant="contained"
            color="error"
            onClick={testToastError}
          >
            Test Error Toast
          </TestButton>
          <TestButton
            variant="contained"
            color="warning"
            onClick={testStringError}
          >
            Test String Error Toast
          </TestButton>
        </Box>
      </TestSection>

      {/* Test Section 3: Inline Errors */}
      <TestSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          📝 Inline Error Tests
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          For form validation errors
        </Typography>
        <Box>
          <TestButton variant="contained" onClick={testInlineError}>
            Show Inline Error
          </TestButton>
        </Box>
        {showInlineError && (
          <Box mt={2}>
            <InlineMessage
              message="This is an inline error message for form validation"
              title="Validation Error"
              onClose={() => setShowInlineError(false)}
            />
          </Box>
        )}
      </TestSection>

      {/* Test Section 4: API Errors */}
      <TestSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          🔌 API Error Tests
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Make sure server is running for these tests
        </Typography>
        <Box>
          <TestButton variant="contained" color="info" onClick={test404Error}>
            Test 404 Not Found
          </TestButton>
        </Box>
      </TestSection>

      {/* Test Section 5: Error Boundary */}
      <TestSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          💥 Error Boundary Test
        </Typography>
        <Typography variant="body2" color="error" paragraph>
          ⚠️ WARNING: This will crash the page intentionally! You'll need to
          refresh.
        </Typography>
        <TestButton
          variant="contained"
          color="error"
          onClick={testErrorBoundary}
        >
          💣 Crash React (Test ErrorBoundary)
        </TestButton>
      </TestSection>

      {/* Console Check */}
      <TestSection>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          🔍 What to Check
        </Typography>
        <Typography variant="body2" component="div">
          After running tests, verify in the console:
          <ul>
            <li>🔴 Structured error logs with emojis</li>
            <li>📡 Network error indicators</li>
            <li>🔥 Server error indicators (if testing 500 errors)</li>
            <li>📭 404 not found indicators</li>
            <li>Context information in logs</li>
            <li>No TypeScript errors</li>
          </ul>
        </Typography>
      </TestSection>

      <Divider sx={{ my: 3 }} />

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        textAlign="center"
      >
        This is a test page. Remove it from production.
      </Typography>
    </TestContainer>
  );
};

export default ErrorTestPage;
